// Wire the UI driver (panel or headless), keyboard shortcuts overlay, and
// per-section deps for the pane bindings. Returns the dispose handle so
// the caller can detach UI on teardown.

import { params } from '@store/instance';
import { initUI as panelInitUI } from '@ui/index';
import { initUI as headlessInitUI } from '@ui/headless';
import { initKeyboardShortcuts } from '@input/shortcuts';
import { toggleColorbarCollapse } from '@ui/colorbar';
import { initializePane } from '@ui/panel/pane';
import { applyDisplaySettings as applySliceDisplay, rebuildAllSlices } from '@scene/slices';
import { onWindowResize } from './input';
import type { Store } from '@store/store';
import type { State } from '@store/schema';
import type { ViewerContext } from '../context';

export function wireUI(store: Store<State>, viewer: ViewerContext): void {
    const keyboardCtx = {
        params,
        keys: viewer.input.keys,
        toggleColorbarCollapse,
    };

    const sectionDeps = {
        params,
        applySliceDisplaySettings: () => applySliceDisplay(viewer, params),
        rebuildSlicesForCurrentGrid: () => rebuildAllSlices(viewer, params),
    };

    const driver = viewer.sceneMeta.ui && viewer.sceneMeta.ui.driver;
    const initUI = (driver === 'headless') ? headlessInitUI : panelInitUI;
    const ui = initUI(store, viewer, {
        onWindowResize: () => onWindowResize(viewer),
        installPaneUI: () => initializePane(viewer, params, {
            applySliceDisplaySettings: () => applySliceDisplay(viewer, params),
            rebuildSlicesForCurrentGrid: () => rebuildAllSlices(viewer, params),
        }),
        initKeyboardShortcuts,
        keyboardCtx,
        sectionDeps,
    });
    viewer.dispose.keyboard = () => ui.dispose();
}
